import React from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import RangeSlider from 'react-range-slider-input';
import { useMap } from 'react-leaflet';

import 'react-range-slider-input/dist/style.css';

import Toggle from 'components/Common/Toggle';
import { MLButton } from 'components/Button/Button';
import { ArchiveDevicesResponse, DevicesOnMapResponse } from 'api/InteractiveMap/types';

interface HeatmapDatePopupProps {
  devices?: DevicesOnMapResponse | ArchiveDevicesResponse | undefined;
  filters: {
    mct: boolean;
    srbm: boolean;
    vulnerabilities: boolean;
    heatmap: boolean;
    archive: boolean;
    encounters: boolean;
    heatmapMct: boolean;
    heatmapSRBM: boolean;
    heatmapSMST: boolean;
    heatmapSRST: boolean;
  };
  onFilterChange: (filter: keyof HeatmapDatePopupProps['filters'], value: boolean) => void;
  pendingStart: string;
  pendingEnd: string;
  setPendingStart: (date: string) => void;
  setPendingEnd: (date: string) => void;
  minDate: Date;
  maxDate: Date;

  minDays: number;
  maxDays: number;
  sliderValue: [number, number];
  handleSliderChange: (vals: [number, number]) => void;
  isHeatmapLoading?: boolean;
  isHeatmapSRBMLoading?: boolean;
  isHeatmapSMSTLoading?: boolean;
  isHeatmapSRSTLoading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

const HeatmapDatePopup: React.FC<HeatmapDatePopupProps> = ({
  devices,
  filters,
  onFilterChange,
  pendingStart,
  pendingEnd,
  setPendingStart,
  setPendingEnd,
  minDate,
  maxDate,
  minDays,
  maxDays,
  sliderValue,
  handleSliderChange,
  isHeatmapLoading,
  isHeatmapSRBMLoading,
  isHeatmapSMSTLoading,
  isHeatmapSRSTLoading,
  onConfirm,
  onClose,
}) => {
  const map = useMap();
  const { t } = useTranslation();
  const isOpenArchive = filters.archive;
  const handleClose = () => {
    onFilterChange('heatmap', false);
    onFilterChange('heatmapMct', false);
    onFilterChange('heatmapSRBM', false);
    onFilterChange('heatmapSRST', false);
    onFilterChange('heatmapSMST', false);
    onClose();
  };

  const disableMapDrag = () => {
    if (map) {
      map.dragging.disable();
    }
  };

  const enableMapDrag = () => {
    if (map) {
      map.dragging.enable();
    }
  };

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        background: 'white',
        border: '1px solid #ccc',
        borderRadius: '8px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
        zIndex: 1,
        cursor: 'default',
      }}
      onClick={e => e.stopPropagation()}
      onWheel={e => e.stopPropagation()}
      onTouchMove={e => e.stopPropagation()}
      onPointerDown={e => e.stopPropagation()}
    >
      <div className="border-bottom border-1">
        <div
          className="d-flex justify-content-between align-items-center  pb-1"
          style={{ padding: '8px 16px', paddingBottom: '0px' }}
        >
          <div>
            <div style={{ fontWeight: 700, fontSize: 18, color: '#3a3a6a' }}>{t('MapSettings.chooseTimePeriod')}</div>
            <div style={{ fontWeight: 400, fontSize: 14, color: '#3a3a6a' }}>{t('MapSettings.heatmapSubtitle')}</div>
          </div>
          <button
            className="btn btn-light waves-effect btn-sm fs-5 fw-semibold"
            style={{ borderRadius: '50%', width: '32px', height: '32px', padding: 0 }}
            onClick={handleClose}
          >
            <i className="ri-close-line" />
          </button>
        </div>
      </div>

      <div style={{ padding: '5px 16px' }}>
        {devices?.mct?.length ? (
          <div style={{ marginBottom: 4 }}>
            <Toggle
              checked={filters.heatmapMct}
              onChange={checked => onFilterChange('heatmapMct', checked)}
              label={t('MapSettings.mctHeatmap')}
              size="sm"
            />
          </div>
        ) : null}
        {devices?.srbm?.length ? (
          <div style={{ marginBottom: 4 }}>
            <Toggle
              checked={filters.heatmapSRBM}
              onChange={checked => onFilterChange('heatmapSRBM', checked)}
              label={t('MapSettings.srbmHeatmap')}
              size="sm"
            />
          </div>
        ) : null}
        {devices?.srst?.length ? (
          <div style={{ marginBottom: 4 }}>
            <Toggle
              checked={filters.heatmapSRST}
              onChange={checked => onFilterChange('heatmapSRST', checked)}
              label={t('MapSettings.srstHeatMap')}
              size="sm"
            />
          </div>
        ) : null}
        {devices?.smst?.length ? (
          <div style={{ marginBottom: 4 }}>
            <Toggle
              checked={filters.heatmapSMST}
              onChange={checked => onFilterChange('heatmapSMST', checked)}
              label={t('MapSettings.smstHeatMap')}
              size="sm"
            />
          </div>
        ) : null}
      </div>

      <div style={{ padding: '5px 16px' }}>
        {!isOpenArchive ? (
          <div>
            <div style={{ fontSize: 13, color: '#3a3a6a', marginBottom: 2 }}>{t('MapSettings.from')}</div>
            <input
              type="date"
              value={pendingStart}
              min={minDate.toISOString().slice(0, 10)}
              max={maxDate.toISOString().slice(0, 10)}
              onChange={e => setPendingStart(e.target.value)}
              style={{
                width: '100%',
                padding: '6px 8px',
                borderRadius: 6,
                border: '1px solid #ccc',
                marginBottom: 8,
              }}
            />
            <div style={{ fontSize: 13, color: '#3a3a6a', marginBottom: 2 }}>{t('MapSettings.to')}</div>
            <input
              type="date"
              value={pendingEnd}
              min={minDate.toISOString().slice(0, 10)}
              max={maxDate.toISOString().slice(0, 10)}
              onChange={e => setPendingEnd(e.target.value)}
              style={{
                width: '100%',
                padding: '6px 8px',
                borderRadius: 6,
                border: '1px solid #ccc',
              }}
            />

            <div style={{ margin: '8px 0 8px 0' }}>
              <div style={{ fontSize: 13, color: '#3a3a6a', marginBottom: 6 }}>{t('MapSettings.dateRange')}</div>
              <div
                style={{ touchAction: 'none', padding: '4px 0' }}
                onPointerDown={e => {
                  e.stopPropagation();
                  disableMapDrag();
                }}
                onPointerUp={e => {
                  e.stopPropagation();
                  enableMapDrag();
                }}
                onPointerLeave={enableMapDrag}
              >
                <RangeSlider
                  min={minDays}
                  max={maxDays}
                  value={sliderValue}
                  onInput={handleSliderChange}
                  step={1}
                  id="range-slider"
                />
              </div>
            </div>
            <MLButton
              color="success"
              isGhost
              classNames="w-100 mt-1"
              innerText={t('MapSettings.confirm')}
              onClick={onConfirm}
              isLoading={isHeatmapLoading || isHeatmapSRBMLoading || isHeatmapSMSTLoading || isHeatmapSRSTLoading}
            />
          </div>
        ) : (
          <div className="pt-2">
            <div
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 6,
                border: '1px solid #ccc',
                backgroundColor: '#f4f4f4',
                color: '#3a3a6a',
                fontSize: 14,
                fontWeight: 600,
                textAlign: 'center',
              }}
            >
              {pendingStart === pendingEnd
                ? `${dayjs(pendingStart).format('DD.MM.YYYY')} (00:00 — 23:59)`
                : `${dayjs(pendingStart).format('DD.MM.YYYY')} — ${dayjs(pendingEnd).format('DD.MM.YYYY')}`}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HeatmapDatePopup;
